import React, { useEffect, useState } from "react";
import { useAuth } from "../../lib/AuthContext";
import XanoAPI from "../../lib/XanoAPI";
import { ClientType } from "../../types";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";

const Statistics: React.FC = () => {
  const auth = useAuth();
  const api = XanoAPI.getInstance();
  const flags = auth.featureFlags;
  const isAllowed = (flag: string) => flags.includes(flag);

  // ✅ State for client counts
  const [clientCounts, setClientCounts] = useState({
    total: 0,
    federal: 0,
    k401: 0,
    b403: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Fetch ALL clients to get accurate counts (with pagination)
  useEffect(() => {
    const fetchClientCounts = async () => {
      try {
        setIsLoading(true);
        
        // Fetch all clients by paginating through all pages
        let allClients: ClientType[] = [];
        let currentPage = 1;
        let hasMorePages = true;
        const perPage = 100; // Reasonable page size
        
        while (hasMorePages) {
          const response = await api.getAllClients(currentPage, "", perPage, {});
          const pageClients = response?.items || [];
          allClients = [...allClients, ...pageClients];
          
          // Check if there are more pages
          hasMorePages = response?.nextPage !== null && response?.nextPage !== undefined;
          currentPage++;
          
          // Safety check to prevent infinite loop
          if (currentPage > 100) {
            console.warn("Statistics: Too many pages, stopping at page 100");
            break;
          }
        }
        
        // Calculate counts from ALL clients
        setClientCounts({
          total: allClients.length,
          federal: allClients.filter((c: ClientType) => c.type === "federal").length,
          k401: allClients.filter((c: ClientType) => c.type === "401k").length,
          b403: allClients.filter((c: ClientType) => c.type === "403b").length,
        });
        
        console.log("✅ Statistics loaded:", {
          totalFetched: allClients.length,
          federal: allClients.filter((c: ClientType) => c.type === "federal").length,
          k401: allClients.filter((c: ClientType) => c.type === "401k").length,
          b403: allClients.filter((c: ClientType) => c.type === "403b").length,
        });
      } catch (error) {
        console.error("Error fetching client statistics:", error);
        // Fallback to 0s
        setClientCounts({
          total: 0,
          federal: 0,
          k401: 0,
          b403: 0,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientCounts();
  }, []); // Run once on mount

  // ✅ Accessing module permissions correctly
  const enabled401kPlan = auth?.currentUser?.module_401k_plan;
  const enabled403bPlan = auth?.currentUser?.module_403b_plan;

  const stats = [
    {
      title: "Total Clients",
      value: isLoading ? "..." : clientCounts.total,
      subtitle: "Total number of clients.",
    },
    {
      title: "Federal Clients",
      value: isLoading ? "..." : clientCounts.federal,
      subtitle: "Federal module clients.",
    },
  ];

  if (isAllowed("401k") && enabled401kPlan) {
    stats.push({
      title: "401k Clients",
      value: isLoading ? "..." : clientCounts.k401,
      subtitle: "401k clients.",
    });
  }

  if (isAllowed("403b") && enabled403bPlan) {
    stats.push({
      title: "403b Clients",
      value: isLoading ? "..." : clientCounts.b403,
      subtitle: "403b clients.",
    });
  }

  return (
    <div className="flex flex-row space-x-2 mb-8">
      {stats.map((stat, index) => (
        <Card key={index} className="flex-1">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-accent">{stat.value}</div>
            <p className="text-xs text-muted-foreground">{stat.subtitle}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default Statistics;
